import { buffer as turfBuffer } from '@turf/turf';
import {
  type Feature as GeoJSONFeature,
  type LineString as GeoJSONLineString,
  type Point as GeoJSONPoint,
  type Polygon as GeoJSONPolygon,
} from 'geojson';
import { type Feature } from 'ol';
import { type Geometry } from 'ol/geom';
import { geoJSONEpsg3857 } from '../../../mapStyleUtils.ts';

/**
 * Converte una feature OpenLayers in un GeoJSON poligonale, applicando un buffer
 * se la geometria è di tipo Point o LineString.
 *
 * - **Polygon**: conversione diretta in GeoJSON.
 * - **Point / LineString**: applica un buffer circolare di `bufferDistance` metri
 *   per ottenere un poligono equivalente (necessario per il calcolo delle intersezioni).
 * - **Altre geometrie**: restituisce `null` (non supportate).
 *
 * @param feature - La feature OpenLayers da convertire.
 * @param bufferDistance - Distanza di buffer in metri per geometrie non-poligonali.
 * @returns Il GeoJSON poligonale risultante, oppure `null` se non convertibile.
 */
export function getBufferedFeatureJSON(
  feature: Feature<Geometry>,
  bufferDistance?: number,
): GeoJSONFeature<GeoJSONPolygon> | null {
  const geomType = feature.getGeometry()?.getType();

  if (geomType === 'Polygon') {
    return geoJSONEpsg3857.writeFeatureObject(
      feature,
    ) as GeoJSONFeature<GeoJSONPolygon>;
  }

  if (
    (geomType === 'Point' || geomType === 'LineString') &&
    bufferDistance &&
    bufferDistance > 0
  ) {
    const rawJson = geoJSONEpsg3857.writeFeatureObject(
      feature,
    ) as GeoJSONFeature<GeoJSONPoint | GeoJSONLineString>;
    const buffered = turfBuffer(rawJson, bufferDistance, { units: 'meters' });
    if (!buffered) return null;
    return buffered as GeoJSONFeature<GeoJSONPolygon>;
  }

  return null;
}
